import { useState, useEffect, useMemo } from "react";
import {
  ChevronRight,
  ChevronDown,
  Circle,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";

export interface ControlNode {
  id: string;
  label?: string; // display badge text, falls back to id if not set
  title: string;
  children?: ControlNode[];
  status?: "pass" | "fail" | "partial" | "untested";
  selected?: boolean;
  hasParams?: boolean;
}

interface ControlTreeViewProps {
  nodes: ControlNode[];
  onSelect?: (node: ControlNode) => void;
  selectedId?: string;
  selectable?: boolean;
  checkable?: boolean;
  checkedIds?: Set<string>;
  onCheck?: (id: string, checked: boolean) => void;
  hideParamsBadge?: boolean;
  /** When true all nodes expand, when false all collapse. undefined = no override. */
  expandAll?: boolean;
  /** Bump this to re-trigger expandAll even if the boolean hasn't changed. */
  expandTrigger?: number;
  /** Callback when expansion state changes - reports if all collapsible nodes are expanded */
  onAllNodesExpandedChange?: (allExpanded: boolean) => void;
}

const statusIcon = {
  pass: <CheckCircle2 className="size-3.5 text-emerald-500 flex-shrink-0" />,
  fail: <XCircle className="size-3.5 text-red-500 flex-shrink-0" />,
  partial: <AlertCircle className="size-3.5 text-amber-500 flex-shrink-0" />,
  untested: <Circle className="size-3.5 d flex-shrink-0" />,
};

/** Count all leaf (non-group) nodes recursively */
function countLeaves(node: ControlNode): number {
  if (!node.children || node.children.length === 0) return 1;
  return node.children.reduce((sum, child) => sum + countLeaves(child), 0);
}

/** Collect all leaf node IDs under a node */
function collectLeafIds(node: ControlNode): string[] {
  if (!node.children || node.children.length === 0) return [node.id];
  return node.children.flatMap(collectLeafIds);
}

interface TreeNodeProps extends ControlTreeViewProps {
  node: ControlNode;
  depth: number;
  expandedNodeIds?: Set<string>;
  onNodeExpand?: (nodeId: string, expanded: boolean) => void;
}

function TreeNode({
  node,
  depth,
  onSelect,
  selectedId,
  selectable,
  checkable,
  checkedIds,
  onCheck,
  hideParamsBadge,
  expandAll,
  expandTrigger,
  expandedNodeIds,
  onNodeExpand,
}: TreeNodeProps) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedId === node.id;
  const isGroup = depth === 0 && hasChildren;

  // For groups: derive checked state from children leaves
  const leafIds = hasChildren ? collectLeafIds(node) : [];
  const checkedLeafCount = hasChildren
    ? leafIds.filter((id) => checkedIds?.has(id)).length
    : 0;
  const isChecked = hasChildren
    ? checkedLeafCount === leafIds.length && leafIds.length > 0
    : (checkedIds?.has(node.id) ?? false);
  const isIndeterminate =
    hasChildren && checkedLeafCount > 0 && checkedLeafCount < leafIds.length;

  // Sync with expandAll prop (trigger changes force re-apply)
  useEffect(() => {
    if (expandAll !== undefined) {
      setExpanded(expandAll);
    }
  }, [expandAll, expandTrigger]);

  // ── Group node (depth 0 with children) - rendered as bordered accordion card ──
  if (isGroup) {
    const leafCount = countLeaves(node);
    return (
      <div className="rounded-md border border-border overflow-hidden">
        {/* Group header */}
        <button
          onClick={() => {
            setExpanded((prev) => {
              const newExpanded = !prev;
              onNodeExpand?.(node.id, newExpanded);
              return newExpanded;
            });
            if (selectable) onSelect?.(node);
          }}
          className={`w-full flex items-center gap-2 px-3 py-2 transition-colors text-left ${
            isSelected
              ? "bg-indigo-50 dark:bg-indigo-500/10"
              : "bg-muted/40 hover:bg-muted/60"
          }`}
        >
          {expanded ? (
            <ChevronDown className="size-3.5 d shrink-0" />
          ) : (
            <ChevronRight className="size-3.5 d shrink-0" />
          )}

          {checkable && (
            <input
              type="checkbox"
              checked={isChecked}
              ref={(el) => {
                if (el) el.indeterminate = !!isIndeterminate;
              }}
              onChange={(e) => {
                e.stopPropagation();
                onCheck?.(node.id, e.target.checked);
              }}
              className="size-3.5 flex-shrink-0 accent-foreground"
              disabled={!onCheck}
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {node.status && statusIcon[node.status]}

          <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400 shrink-0">
            {node.label ?? node.id}
          </span>
          <span className="text-xs font-semibold truncate">{node.title}</span>
          <span className="ml-auto text-[10px] d shrink-0">
            {checkable ? `${checkedLeafCount} / ${leafCount}` : leafCount}
          </span>
        </button>

        {/* Children */}
        {expanded && (
          <div className="divide-y divide-border">
            {node.children!.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                onSelect={onSelect}
                selectedId={selectedId}
                selectable={selectable}
                checkable={checkable}
                checkedIds={checkedIds}
                onCheck={onCheck}
                hideParamsBadge={hideParamsBadge}
                expandAll={expandAll}
                expandTrigger={expandTrigger}
                nodes={[]}
                expandedNodeIds={expandedNodeIds}
                onNodeExpand={onNodeExpand}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Leaf / nested child node ──
  return (
    <div>
      <div
        className={`flex items-center gap-2 py-1.5 text-sm transition-colors cursor-pointer ${
          isSelected
            ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-800 dark:text-indigo-200"
            : "hover:bg-accent"
        }`}
        style={{ paddingLeft: `${12 + depth * 16}px`, paddingRight: "12px" }}
        onClick={() => {
          if (hasChildren) setExpanded((prev) => !prev);
          if (selectable) onSelect?.(node);
        }}
      >
        {/* Expand toggle for nested groups */}
        {hasChildren ? (
          <span
            className="flex-shrink-0 size-4 flex items-center justify-center cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
          >
            {expanded ? (
              <ChevronDown className="size-3.5 d" />
            ) : (
              <ChevronRight className="size-3.5 d" />
            )}
          </span>
        ) : (
          <span className="flex-shrink-0 size-4" />
        )}

        {checkable && (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => {
              e.stopPropagation();
              onCheck?.(node.id, e.target.checked);
            }}
            className="size-3.5 flex-shrink-0 accent-foreground"
            onClick={(e) => e.stopPropagation()}
          />
        )}

        {node.status && statusIcon[node.status]}

        <span
          className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${
            isSelected
              ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
              : "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400"
          }`}
        >
          {node.label ?? node.id}
        </span>
        <span className="text-xs truncate">{node.title}</span>
        {node.hasParams && !hideParamsBadge && (
          <span
            className={`ml-auto flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
              isSelected
                ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
                : "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400"
            }`}
          >
            Params
          </span>
        )}
      </div>

      {hasChildren && expanded && (
        <div>
          {node.children!.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              onSelect={onSelect}
              selectedId={selectedId}
              selectable={selectable}
              checkable={checkable}
              checkedIds={checkedIds}
              onCheck={onCheck}
              hideParamsBadge={hideParamsBadge}
              expandAll={expandAll}
              nodes={[]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ControlTreeView(props: ControlTreeViewProps) {
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set());

  // ── Edge Case Handling for Expand/Collapse Tracking ──
  // This component manages node expansion state and reports when all collapsible nodes are expanded.
  //
  // Edge Cases Handled:
  // 1. Empty tree: allCollapsibleNodesExpanded is false if no collapsible nodes exist
  // 2. Tree structure changes: expandedNodeIds is pruned to remove stale node IDs
  // 3. Node ID validation: verifies node exists before tracking expansion
  // 4. Callback definition: depends on callback reference only, not entire props object
  // 5. Multiple instances: each ControlTreeView maintains independent tracking
  // 6. Stale closures: uses memoization and proper effect dependencies
  // 7. Performance: uses useMemo to avoid recalculating collapsible nodes on every render

  // Collect all collapsible node IDs recursively
  const collectCollapsibleNodeIds = (nodes: ControlNode[]): string[] => {
    return nodes.flatMap((node) => {
      const ids: string[] = [];
      if (node.children && node.children.length > 0) {
        ids.push(node.id);
        ids.push(...collectCollapsibleNodeIds(node.children));
      }
      return ids;
    });
  };

  // Memoize collapsible node IDs to avoid unnecessary computations
  const collapsibleNodeIds = useMemo(
    () => collectCollapsibleNodeIds(props.nodes),
    [props.nodes]
  );

  // Reset expandedNodeIds when tree structure changes (filter/search/import change)
  // EDGE CASE: Prune expandedNodeIds to only include nodes that still exist in tree
  useEffect(() => {
    setExpandedNodeIds((prev) => {
      // Filter out node IDs that no longer exist in the new tree
      const newIds = new Set(prev);
      let hasChanged = false;
      prev.forEach((id) => {
        if (!collapsibleNodeIds.includes(id)) {
          newIds.delete(id);
          hasChanged = true;
        }
      });
      return hasChanged ? newIds : prev;
    });
  }, [collapsibleNodeIds]);

  // Compute if all collapsible nodes are expanded
  // EDGE CASE: Handle empty tree (no collapsible nodes)
  const allCollapsibleNodesExpanded =
    collapsibleNodeIds.length > 0 &&
    collapsibleNodeIds.every((id) => expandedNodeIds.has(id));

  // Call callback when all nodes are expanded
  // EDGE CASE: Only call if callback is defined to avoid errors
  useEffect(() => {
    if (props.onAllNodesExpandedChange) {
      props.onAllNodesExpandedChange(allCollapsibleNodesExpanded);
    }
  }, [allCollapsibleNodesExpanded, props.onAllNodesExpandedChange]);

  // Handle node expansion changes
  const handleNodeExpand = (nodeId: string, expanded: boolean) => {
    setExpandedNodeIds((prev) => {
      // EDGE CASE: Verify node exists before tracking
      if (!collapsibleNodeIds.includes(nodeId)) {
        return prev;
      }
      const next = new Set(prev);
      if (expanded) {
        next.add(nodeId);
      } else {
        next.delete(nodeId);
      }
      return next;
    });
  };

  return (
    <div className="select-none space-y-1">
      {props.nodes.map((node) => (
        <TreeNode
          key={node.id}
          {...props}
          node={node}
          depth={0}
          expandedNodeIds={expandedNodeIds}
          onNodeExpand={handleNodeExpand}
        />
      ))}
    </div>
  );
}
